import { useState } from "react"

import { MutationOptions } from "api/api.types"
import { useDialog } from "hooks/dialog"
import { useCustomSnackbar } from "hooks/snackbar"
import { useTranslation } from "react-i18next"
import { IDeleteTransactionsRequest } from "ts/interfaces/Transaction"
import { getErrorMessageFromTab } from "utils/api"
import { getDeletePayload, Selection } from "utils/transactionSelection"

import { DeleteTransactionsView } from "./deleteTransactionsTaskStorage"
import { useDeleteTransactionsTask } from "./useDeleteTransactionsTask"

interface IUseTransactionsDeleteFlowArgs {
  view: DeleteTransactionsView
  scopeId: number | undefined
  onDeleted: () => void
}

/**
 * The delete flow both transaction lists share: which ids the confirmation
 * dialog is about, what a DELETE response leads to (toast + reload, or a
 * background task), and the task itself.
 */
export const useTransactionsDeleteFlow = ({
  view,
  scopeId,
  onDeleted,
}: IUseTransactionsDeleteFlowArgs) => {
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const { activeTask, startTask, resetLists } = useDeleteTransactionsTask({
    view,
    scopeId,
  })
  const { isOpen, handleOpen, handleClose } = useDialog()
  const [request, setRequest] = useState<IDeleteTransactionsRequest | null>(
    null
  )

  const openDialog = (next: IDeleteTransactionsRequest) => {
    setRequest(next)
    handleOpen()
  }

  const openSingle = (id: number, title: string) =>
    openDialog({ ids: [id], target: { kind: "single", title } })

  const openBatch = (selection: Selection) => {
    const ids = getDeletePayload(selection)
    openDialog({
      ids,
      target: {
        kind: "batch",
        count: ids.length,
        scope:
          selection.kind !== "allMatching"
            ? "rows"
            : selection.excluded.length
              ? "allMatchingExcept"
              : "allMatching",
      },
    })
  }

  const mutationOptions: MutationOptions<
    { task_id: string | null },
    { transaction_ids: number[] }
  > = {
    onSuccess: (response, variables) => {
      onDeleted()

      if (response.data.task_id) {
        startTask(response.data.task_id, variables.transaction_ids.length)
        return
      }

      showSnackbar({
        title:
          request?.target.kind === "single"
            ? t("allTransactions.transactionDeleted")
            : t("allTransactions.transactionsDeleted", {
                count: variables.transaction_ids.length,
              }),
      })
      resetLists()
    },
    onError: (err) => {
      showSnackbar({
        title: t(getErrorMessageFromTab(err)),
        variant: "error",
      })
    },
    onSettled: () => {
      handleClose()
    },
  }

  return {
    activeTask,
    request,
    openSingle,
    openBatch,
    mutationOptions,
    dialog: {
      open: isOpen,
      target: request?.target ?? null,
      onClose: handleClose,
    },
  }
}
